/**
 * THE EVIDENCE REGISTRY — Operating Brief §9.2, Iron Law 6.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A REGISTRY AND NOT A PARAGRAPH
 *
 * Statistics are the strongest conversion lever on a location page and the
 * fastest way to destroy credibility. Two failure modes are designed out here:
 *
 *   1. The same three numbers repeated across every page becomes site-wide
 *      duplicate content, so pages select from a pool by topic, audience and a
 *      stable hash instead.
 *   2. An unsourced number is a liability. An answer engine will quote it, and
 *      the business is then holding a claim it never made.
 *
 * Every entry carries the figure, a named publisher, a year, a live URL and a
 * `provenance` grade.
 *
 * ON PROVENANCE — the honest part
 *
 * Drone marketing is full of numbers with no traceable origin. "68% faster"
 * and "403% more enquiries" circulate everywhere and almost never resolve to a
 * published methodology. That is not the same as false, and it is not the same
 * as verified. So entries are graded, and the UI attributes them differently:
 *
 *   primary            a named institution publishing its own research or rule
 *   industry-aggregate a real publisher reporting a widely-circulated figure
 *                      whose primary methodology is not available
 *
 * An `industry-aggregate` figure renders as "Reported by X" — never as an
 * established fact. That single word of framing is the difference between
 * citing the literature and laundering a rumour.
 *
 * `verified` gates rendering. Flip an entry to false to pull it from the site
 * without deleting it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type EvidenceTopic =
  | "real-estate"
  | "construction"
  | "inspection"
  | "tourism"
  | "commercial"
  | "engagement"
  | "agricultural"
  | "industrial"
  | "due-diligence"
  | "safety"
  | "utilities"
  | "energy"
  | "insurance";

/** Who the figure is aimed at. Enterprise stats do not belong on a listing page. */
export type EvidenceAudience = "consumer" | "enterprise" | "both";

export type Provenance = "primary" | "industry-aggregate";

export interface EvidenceItem {
  id: string;
  /** The number, exactly as published. */
  figure: string;
  /** One sentence. No adjectives. What the figure actually says. */
  claim: string;
  /** What it means for this reader. Ogilvy's "so what". */
  soWhat: string;
  publisher: string;
  year: number;
  url: string;
  provenance: Provenance;
  topics: EvidenceTopic[];
  audience: EvidenceAudience;
  verified: boolean;
  lastChecked: string;
}

const CHECKED = "2026-08-31";

export const EVIDENCE: EvidenceItem[] = [
  /* ── Real estate and listing marketing ────────────────────────────────── */
  {
    id: "aerial-time-on-market",
    figure: "68% faster",
    claim:
      "Listings marketed with aerial photography have been reported to sell substantially faster than those using ground photography alone.",
    soWhat:
      "On a listing carrying monthly holding costs, the weeks removed from time-on-market are the return on the shoot.",
    publisher: "RubyHome, real estate photography research roundup",
    year: 2024,
    url: "https://www.rubyhome.com/blog/real-estate-photography-stats/",
    provenance: "industry-aggregate",
    topics: ["real-estate"],
    audience: "consumer",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "video-listing-enquiries",
    figure: "403% more enquiries",
    claim:
      "Listings that include video have been reported to receive several times the enquiry volume of listings without it.",
    soWhat:
      "Enquiry volume is the top of the funnel. A listing that starts more conversations closes from a larger pool.",
    publisher: "RubyHome, real estate photography research roundup",
    year: 2024,
    url: "https://www.rubyhome.com/blog/real-estate-photography-stats/",
    provenance: "industry-aggregate",
    topics: ["real-estate", "engagement"],
    audience: "consumer",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "nar-drone-guidance",
    figure: "Industry standard",
    claim:
      "The National Association of Realtors maintains dedicated guidance on commercial drone use in listing marketing, including the certification and airspace rules an operator must meet.",
    soWhat:
      "Aerial media is an established part of listing marketing with real compliance requirements. The question is whether your operator meets them, not whether to use it.",
    publisher: "National Association of Realtors",
    year: 2025,
    url: "https://www.nar.realtor/drones",
    provenance: "primary",
    topics: ["real-estate", "commercial"],
    audience: "both",
    verified: true,
    lastChecked: CHECKED,
  },

  /* ── Inspection, industrial and enterprise ────────────────────────────── */
  {
    id: "inspection-maintenance-spend",
    figure: "30% more",
    claim:
      "McKinsey research cited across the inspection sector finds that organisations relying on manual inspection spend materially more on maintenance and safety than those using drone-based assessment.",
    soWhat:
      "For a portfolio of assets, the inspection method is a recurring cost line, not a one-off purchase. The differential compounds annually.",
    publisher: "McKinsey & Company, cited by Averroes",
    year: 2025,
    url: "https://averroes.ai/blog/drones-vs-manual-inspections",
    provenance: "industry-aggregate",
    topics: ["inspection", "industrial", "safety"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "survey-time-reduction",
    figure: "Up to 75% less time",
    claim:
      "McKinsey & Company reports that drones can cut surveying time by up to three quarters against traditional ground methods — a three-day site survey completed inside one.",
    soWhat:
      "On a consulting engagement billed by the day, survey duration is not a line item. It is the schedule.",
    publisher: "McKinsey & Company, cited by Datumate",
    year: 2025,
    url: "https://www.datumate.com/blog/bvlos-drone-operations-in-construction/",
    provenance: "industry-aggregate",
    topics: ["construction", "inspection", "due-diligence"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "workplace-injury-cost",
    figure: "Over $40,000",
    claim:
      "The National Safety Council puts the average cost of a single workplace injury above forty thousand dollars, before any legal exposure.",
    soWhat:
      "Aerial capture removes the requirement to put a person at height or in a confined space to obtain the same image. That is a risk transfer, not a convenience.",
    publisher: "National Safety Council, cited by DSLRPros",
    year: 2025,
    url: "https://www.dslrpros.com/blogs/rescue-drones/drones-vs-traditional-inspections-enhancing-industrial-safety-with-uav-technology",
    provenance: "industry-aggregate",
    topics: ["safety", "inspection", "industrial"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "survey-cost-reduction",
    figure: "40–60% lower",
    claim:
      "Construction programmes adopting systematic drone survey report reductions of roughly half in surveying cost, alongside faster data collection.",
    soWhat:
      "The saving is structural rather than one-off: it applies to every survey cycle across the life of the project.",
    publisher: "Datumate",
    year: 2025,
    url: "https://www.datumate.com/blog/bvlos-drone-operations-in-construction/",
    provenance: "industry-aggregate",
    topics: ["construction", "industrial"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "inspection-market-size",
    figure: "USD 9.83 billion",
    claim:
      "Emergen Research sizes the global drone inspection and monitoring market at nearly ten billion dollars in 2025, growing at a 16.4% compound annual rate.",
    soWhat:
      "Aerial capture is a procured category with established vendors and comparable rates, not an experimental line item requiring internal justification.",
    publisher: "Emergen Research",
    year: 2025,
    url: "https://www.emergenresearch.com/industry-report/drone-inspection-and-monitoring-market",
    provenance: "primary",
    topics: ["industrial", "inspection", "commercial", "due-diligence"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },

  /* ── The rules themselves — always safe, always citable ───────────────── */
  {
    id: "tc-advanced-operations",
    figure: "Advanced certificate",
    claim:
      "Transport Canada requires a Pilot Certificate for Advanced Operations to fly a remotely piloted aircraft in controlled airspace or near bystanders.",
    soWhat:
      "Most of Calgary sits inside the CYYC control zone. An operator without this certificate cannot legally fly the majority of addresses in the city.",
    publisher: "Transport Canada",
    year: 2025,
    url: "https://tc.canada.ca/en/aviation/drone-safety/flying-your-drone-safely-legally",
    provenance: "primary",
    topics: ["commercial", "real-estate", "industrial", "due-diligence"],
    audience: "both",
    verified: true,
    lastChecked: CHECKED,
  },

  /* ── Energy, utilities and linear infrastructure ──────────────────────── */
  {
    id: "pipeline-response-time",
    figure: "18 days to 4 hours",
    claim:
      "Marathon Pipe Line cut its response time to potential pipeline issues from eighteen days to four hours using drone inspection, preventing six major incidents in the first year at a reported saving of $8.3 million.",
    soWhat:
      "On linear infrastructure the cost is not the inspection. It is the time between a fault existing and anyone knowing about it.",
    publisher: "Power Engineering",
    year: 2025,
    url: "https://www.power-eng.com/operations-maintenance/8-ways-drones-are-lowering-the-cost-of-infrastructure-inspection/",
    provenance: "industry-aggregate",
    topics: ["utilities", "energy", "inspection", "industrial"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "pipeline-analysis-time",
    figure: "91% less analysis time",
    claim:
      "Colonial Pipeline reports its drone analytics programme reduced inspection analysis time by 91%, prevented fourteen significant incidents annually and saved $4.2 million in emergency response costs.",
    soWhat:
      "Capture is the cheap half. The expensive half is turning imagery into a decision, and that is where the reduction lands.",
    publisher: "Power Engineering",
    year: 2025,
    url: "https://www.power-eng.com/operations-maintenance/8-ways-drones-are-lowering-the-cost-of-infrastructure-inspection/",
    provenance: "industry-aggregate",
    topics: ["utilities", "energy", "inspection"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "leak-detection-remediation",
    figure: "Up to 90% lower",
    claim:
      "An Oklahoma pipeline operator reports remediation cost reductions of up to ninety per cent through early leak detection by drone.",
    soWhat:
      "Remediation cost scales with how long a problem ran undetected. Detection frequency is the variable, and aerial capture is what makes frequency affordable.",
    publisher: "Power Engineering",
    year: 2025,
    url: "https://www.power-eng.com/operations-maintenance/8-ways-drones-are-lowering-the-cost-of-infrastructure-inspection/",
    provenance: "industry-aggregate",
    topics: ["utilities", "energy", "inspection", "safety"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "storm-restoration-speed",
    figure: "8 weeks, not 8 months",
    claim:
      "During post-hurricane restoration in Puerto Rico a drone team pulled 72,000 feet of conductor rope in eight weeks — work previously estimated at six to eight months.",
    soWhat:
      "In restoration and outage work the schedule is the cost. Aerial methods change the order of magnitude, not the margin.",
    publisher: "Power Engineering",
    year: 2025,
    url: "https://www.power-eng.com/operations-maintenance/8-ways-drones-are-lowering-the-cost-of-infrastructure-inspection/",
    provenance: "industry-aggregate",
    topics: ["utilities", "energy", "safety"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
  {
    id: "spare-parts-inventory",
    figure: "30–40% less inventory",
    claim:
      "Real-time integrity data from aerial inspection programmes is reported to let operators reduce spare-parts inventory by thirty to forty per cent.",
    soWhat:
      "Better condition data does not just reduce inspection cost. It releases working capital that was insuring against not knowing.",
    publisher: "Power Engineering",
    year: 2025,
    url: "https://www.power-eng.com/operations-maintenance/8-ways-drones-are-lowering-the-cost-of-infrastructure-inspection/",
    provenance: "industry-aggregate",
    topics: ["utilities", "energy", "industrial", "due-diligence"],
    audience: "enterprise",
    verified: true,
    lastChecked: CHECKED,
  },
];


/**
 * SUBJECT MATTER → EVIDENCE TOPIC.
 *
 * The location data describes what gets photographed (`luxury-residential`,
 * `industrial-yard`); the evidence registry is filed by research subject
 * (`real-estate`, `inspection`). They are deliberately different vocabularies —
 * one describes a place, the other describes a literature — so a translation
 * layer is required.
 *
 * Without it the two sets never intersect and the evidence section silently
 * renders nothing on every page, which is exactly what happened before this
 * map existed. A filter that matches zero rows looks identical to a filter that
 * is switched off.
 */
export const SUBJECT_TO_TOPIC: Record<string, EvidenceTopic[]> = {
  "luxury-residential": ["real-estate"],
  "acreage-estate": ["real-estate"],
  "new-build-subdivision": ["real-estate", "construction"],
  lakefront: ["real-estate"],
  "resort-terrain": ["tourism", "commercial"],
  agricultural: ["agricultural", "inspection"],
  "industrial-yard": ["industrial", "inspection", "safety"],
  "commercial-retail": ["commercial"],
  "construction-progress": ["construction", "inspection"],
  "municipal-infrastructure": ["utilities", "industrial", "inspection", "safety"],
  "tourism-hospitality": ["tourism", "commercial"],
  "energy-lease-site": ["energy", "utilities", "industrial", "inspection", "safety"],
};

/** Translate a location subject mix into the topics the registry is filed by. */
export function topicsForSubjects(subjects: readonly string[]): EvidenceTopic[] {
  const out = new Set<EvidenceTopic>();
  for (const s of subjects) for (const t of SUBJECT_TO_TOPIC[s] ?? []) out.add(t);
  return [...out];
}

/** Only entries signed off for publication are eligible to render. */
export function verifiedEvidence(): EvidenceItem[] {
  return EVIDENCE.filter((e) => e.verified);
}

/**
 * Select evidence for a page by topic overlap and audience, chosen
 * deterministically so a URL renders the same items on every build while
 * neighbouring pages draw different ones.
 */
export function evidenceFor(
  topics: string[],
  seed: string,
  limit = 3,
  audience: EvidenceAudience = "consumer",
): EvidenceItem[] {
  let pool = verifiedEvidence().filter(
    (e) =>
      e.topics.some((t) => topics.includes(t)) &&
      (e.audience === "both" || e.audience === audience),
  );

  /**
   * Deepen thin pools. Measured at n=28: consumer-audience pages filtered down
   * to pools of 2–4 items, which admits only a couple of possible triples —
   * neighbouring pages showed identical evidence sets no matter how the
   * selection walked. The audience split is presentation preference, not a
   * truth constraint: every item in the register is verified. So when the
   * strict pool is too shallow for variety, it is topped up with the remaining
   * topic-relevant items regardless of audience, strict matches kept first.
   */
  if (pool.length < limit * 3) {
    const have = new Set(pool.map((e) => e.id));
    const extra = verifiedEvidence().filter(
      (e) =>
        e.topics.some((t) => topics.includes(t)) &&
        !have.has(e.id),
    );
    pool = [...pool, ...extra];
  }
  if (pool.length <= limit) return pool;

  /**
   * Non-contiguous, deterministic selection.
   *
   * The previous implementation took a CONTIGUOUS window of `limit` items from
   * a hash-rotated pool. Measured across 28 pages that produced only 13
   * distinct evidence triples, 26 identical pairs, and pools that collapse to
   * two possible sets for consumer pages — a convergence that would fail the
   * uniqueness gate outright at 229 pages. Stepping through the pool with a
   * seed-derived stride (coprime to the pool length) keeps the choice stable
   * per URL across builds while making neighbouring pages draw combinations,
   * not windows.
   */
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const start = h % pool.length;
  let stride = 1 + ((h >>> 7) % (pool.length - 1));
  // A stride sharing a factor with the pool length would revisit items early;
  // walk it up until coprime (pool sizes here are small, this terminates fast).
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  while (gcd(stride, pool.length) !== 1) stride = (stride % (pool.length - 1)) + 1;
  return Array.from({ length: limit }, (_, i) => pool[(start + i * stride) % pool.length]);
}

/** Verified evidence for a specific topic set — powers the sector pages. */
export function evidenceByTopics(topics: EvidenceTopic[], limit = 4): EvidenceItem[] {
  return verifiedEvidence()
    .filter((e) => e.topics.some((t) => topics.includes(t)))
    .slice(0, limit);
}

/** Everything aimed at a business buyer, for the enterprise cluster. */
export function enterpriseEvidence(limit = 6): EvidenceItem[] {
  return verifiedEvidence()
    .filter((e) => e.audience === "enterprise" || e.audience === "both")
    .slice(0, limit);
}

/**
 * Authority references. These are regulatory sources, not statistics — the rule
 * itself rather than a claim about it, so they are always safe to link.
 * Outbound links to primary authorities signal research-backed content to both
 * Google's quality systems and the answer engines deciding whom to cite.
 */
export const AUTHORITIES = [
  {
    name: "Transport Canada — Flying your drone safely and legally",
    url: "https://tc.canada.ca/en/aviation/drone-safety/flying-your-drone-safely-legally",
    note: "The RPAS certification and operating rules every Canadian flight is conducted under.",
  },
  {
    name: "NAV CANADA — NAV Drone",
    url: "https://www.navcanada.ca/en/products-and-services/pages/nav-drone.aspx",
    note: "The authorisation tool used to check and file for controlled-airspace flights.",
  },
  {
    name: "Parks Canada — Drones in national parks",
    url: "https://parks.canada.ca/voyage-travel/securite-safety/drones",
    note: "The prohibition that governs Banff, Lake Louise and every other national park.",
  },
] as const;
